import React, { useEffect, useMemo, useState } from 'react';
import { Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Get } from '../../Axios/AxiosFunctions';
import { BaseURL } from '../../config/apiUrl';
import { Button } from '../../Component/Button/Button';
import { DropDown } from '../../Component/DropDown/DropDown';
import { Input } from '../../Component/Input/Input';
import DeadlineSchedulePreview from '../../Component/DeadlineSchedulePreview';
import ModalSkeleton from '../ModalSkeleton';
import classes from './AddProjectModal.module.css';

/** Today as YYYY-MM-DD, read locally so the default matches the user's day. */
function todayString() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

const AddProjectModal = ({
  show,
  setShow,
  handleSubmit,
  data,
  isLoading = false,
  selectedListing = null,
  selectedListings = [],
  assignees = [],
}) => {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer || {},
  );

  const [projectName, setProjectName] = useState('');
  const [listingName, setListingName] = useState('');
  const [assignTo, setAssignTo] = useState([]);

  // Deal schedule. Only offered when creating -- applying a template to an
  // existing project is what the Apply Template action on the board is for,
  // and doing it from Edit would silently re-cascade a live deal.
  const isCreate = data == null;
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [anchorDate, setAnchorDate] = useState(todayString());
  const [dayBasis, setDayBasis] = useState('business');
  const [overrides, setOverrides] = useState([]);

  const basisOptions = useMemo(
    () => [
      { label: 'Business days (skip weekends & holidays)', value: 'business' },
      { label: 'Calendar days', value: 'calendar' },
    ],
    [],
  );

  useEffect(() => {
    if (!isCreate) return;
    Get(BaseURL('project-templates'), accessToken, companyId).then((res) => {
      setTemplates(res?.data?.templates || []);
    });
  }, [isCreate, accessToken, companyId]);

  const templateSlug = selectedTemplate?.slug;

  // Hand-set dates are keyed to the chosen template's tasks and measured from
  // the anchor, so changing either invalidates them.
  useEffect(() => {
    setOverrides([]);
  }, [selectedTemplate, anchorDate]);

  useEffect(() => {
    if (!!data) {
      let listObject = selectedListings.find(
        (x) => x?._id == data?.business?._id,
      );
      setProjectName(data?.name);
      setListingName(listObject);
      setAssignTo(data?.assignTo);
    } else if (!!selectedListing) {
      let listObject = selectedListings.find((x) => x?._id == selectedListing);
      setListingName(listObject);
    }
  }, [data]);

  useEffect(() => {
    if (selectedListing) {
      let listObject = selectedListings.find((x) => x?._id == selectedListing);

      let listBrokers = [];

      listBrokers = assignees?.[0]?.filter((item) =>
        listObject?.broker?.includes(item?._id),
      );

      setAssignTo(listBrokers);
    }
  }, [selectedListing]);

  const HandleSubmitData = () => {
    const newAssignToArray = assignTo?.map((item) => item?._id);

    // The schedule travels as a separate argument, not inside the project
    // payload: the caller's required-field loop rejects any empty value, and
    // an optional template must not make project creation fail.
    handleSubmit(
      {
        name: projectName,
        assignTo: newAssignToArray,
        business: listingName?._id,
      },
      listingName?.status,
      isCreate && templateSlug
        ? { templateSlug, anchorDate, dayBasis, overrides }
        : null,
    );
  };

  return (
    <>
      <style>{`
        .modal-content{
          overflow:visible !important;
        }
      `}</style>
      <div>
        <ModalSkeleton
          show={show}
          setShow={setShow}
          width="700px"
          borderRadius="20px"
          header={data == null ? 'Add Project' : 'Edit Project'}
          showCloseIcon={true}
        >
          <div className={classes.addProjectModal_main}>
            <Row className={classes.addProject_row}>
              <Input
                setter={setProjectName}
                value={projectName}
                placeholder={'Project Name'}
                autoFocus={true}
              />
              <DropDown
                setter={(e) => {
                  setListingName(e);

                  let listBrokers = [];

                  listBrokers = assignees?.[0]?.filter((item) =>
                    e?.broker?.includes(item?._id),
                  );

                  setAssignTo(listBrokers);
                }}
                value={listingName}
                placeholder={'Listing Name'}
                options={selectedListings}
                optionLabel={'companyName'}
                optionValue={'companyName'}
                disabled={selectedListing}
              />
              <DropDown
                setter={setAssignTo}
                value={assignTo}
                isMulti
                placeholder={'Assigned To'}
                options={assignees[0]}
                getOptionLabel={(option) =>
                  option?.firstName + ' ' + option?.lastName
                }
                getOptionValue={(option) =>
                  option?.firstName + ' ' + option?.lastName
                }
              />
            </Row>

            {isCreate && (
              <>
                <Row className={classes.addProject_row}>
                  <DropDown
                    setter={setSelectedTemplate}
                    value={selectedTemplate}
                    placeholder={'Task template (optional)'}
                    options={templates}
                    optionLabel={'name'}
                    optionValue={'name'}
                  />
                </Row>

                {templateSlug && (
                  <>
                    <Row className={classes.addProject_row}>
                      <div className={classes.fieldBlock}>
                        <Input
                          value={anchorDate}
                          setter={setAnchorDate}
                          label={'Mutual execution date'}
                          placeholder={'Mutual execution date'}
                          type={'date'}
                        />
                        <p className={classes.fieldHint}>
                          Every deadline below is counted from this date.
                        </p>
                      </div>
                    </Row>
                    <Row className={classes.addProject_row}>
                      <DropDown
                        setter={(v) => setDayBasis(v?.value || 'business')}
                        value={basisOptions.find((o) => o.value === dayBasis)}
                        placeholder={'Counting basis'}
                        options={basisOptions}
                        optionLabel={'label'}
                        optionValue={'value'}
                      />
                    </Row>
                  </>
                )}

                <DeadlineSchedulePreview
                  templateSlug={templateSlug}
                  anchorDate={anchorDate}
                  dayBasis={dayBasis}
                  active={show && isCreate}
                  overrides={overrides}
                  onOverridesChange={setOverrides}
                />
              </>
            )}

            <div className={classes.btn_main}>
              <Button
                onClick={() => HandleSubmitData()}
                className={classes.btn}
                label={
                  isLoading
                    ? 'Submitting...'
                    : data == null
                      ? 'Add Project'
                      : 'Edit Project'
                }
                disabled={isLoading}
              />
            </div>
          </div>
        </ModalSkeleton>
      </div>
    </>
  );
};

export default AddProjectModal;
